import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  TextField,
  Button,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

function HistoricalCallLogs() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Date range filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  
  // Additional filter states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [direction, setDirection] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState({
    completed: true,
    failed: true,
    busy: true,
    'no-answer': true,
    'in-progress': true,
    canceled: true,
    ringing: true,
    queued: true
  });
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  
  // Filtered calls state
  const [filteredCalls, setFilteredCalls] = useState([]);
  
  useEffect(() => {
    if (useCustomDateRange) {
      if (startDate && endDate) {
        fetchCallsByDateRange(startDate, endDate, page, rowsPerPage);
      }
    } else {
      fetchHistoricalCalls(timeRange, page, rowsPerPage);
    }
  }, [timeRange, page, rowsPerPage, useCustomDateRange, startDate, endDate]);
  
  useEffect(() => {
    // Apply client-side filtering
    filterCalls();
  }, [calls, phoneNumber, direction, selectedStatuses]);
  
  const fetchHistoricalCalls = async (days, page, limit) => {
    setLoading(true);
    try {
      const response = await axios.get(`/twilio/historical-call-logs?days=${days}&page=${page}&limit=${limit}`);
      setCalls(response.data.calls);
      setTotalCount(response.data.totalCount || response.data.calls.length);
      setLoading(false);
    } catch (err) {
      setError('Failed to load historical call logs');
      setLoading(false);
      console.error('Historical call logs fetch error:', err);
    }
  };
  
  const fetchCallsByDateRange = async (start, end, page, limit) => {
    setLoading(true);
    try {
      const startTime = start.toISOString();
      const endTime = end.toISOString();
      const response = await axios.get(
        `/twilio/historical-call-logs?startDate=${startTime}&endDate=${endTime}&page=${page}&limit=${limit}`
      );
      setCalls(response.data.calls);
      setTotalCount(response.data.totalCount || response.data.calls.length);
      setLoading(false);
    } catch (err) {
      setError('Failed to load historical call logs');
      setLoading(false);
      console.error('Historical call logs fetch error:', err);
    }
  };
  
  const filterCalls = () => {
    if (!calls || calls.length === 0) {
      setFilteredCalls([]);
      return;
    }
    
    let result = [...calls];
    
    // Filter by phone number
    if (phoneNumber.trim()) {
      const searchTerm = phoneNumber.trim().toLowerCase();
      result = result.filter(call => 
        (call.from && call.from.toLowerCase().includes(searchTerm)) ||
        (call.to && call.to.toLowerCase().includes(searchTerm))
      );
    }
    
    // Filter by direction
    if (direction !== 'all') {
      result = result.filter(call => call.direction === direction);
    }
    
    // Filter by status
    const activeStatuses = Object.keys(selectedStatuses).filter(status => selectedStatuses[status]);
    if (activeStatuses.length > 0) {
      result = result.filter(call => 
        call.status && activeStatuses.includes(call.status.toLowerCase())
      );
    }
    
    setFilteredCalls(result);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleApplyDateFilter = () => {
    if (startDate && endDate) {
      setUseCustomDateRange(true);
      setPage(0);
    }
  };
  
  const handleClearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setUseCustomDateRange(false);
    setPage(0);
  };
  
  const handleStatusChange = (status) => {
    setSelectedStatuses(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };
  
  const handleSelectAllStatuses = (select) => {
    const newStatuses = {};
    Object.keys(selectedStatuses).forEach(status => {
      newStatuses[status] = select;
    });
    setSelectedStatuses(newStatuses);
  };
  
  const clearAllFilters = () => {
    setPhoneNumber('');
    setDirection('all');
    handleSelectAllStatuses(true);
  };
  
  const refreshData = () => {
    if (useCustomDateRange) {
      if (startDate && endDate) {
        fetchCallsByDateRange(startDate, endDate, page, rowsPerPage);
      }
    } else {
      fetchHistoricalCalls(timeRange, page, rowsPerPage);
    }
  };
  
  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'busy':
        return 'warning';
      case 'no-answer':
        return 'warning';
      case 'in-progress':
        return 'info';
      case 'canceled':
        return 'error';
      case 'ringing':
        return 'primary';
      case 'queued':
        return 'secondary';
      default:
        return 'default';
    }
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  if (loading && calls.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  const displayedCalls = filteredCalls.length > 0 ? filteredCalls : calls;
  
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Historical Call Logs
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={refreshData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Typography variant="body1" paragraph>
        These call records are retrieved directly from Twilio's API and show all calls made through your Twilio account.
      </Typography>
      
      {/* Date Range Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Date Range
        </Typography>
        <Grid container spacing={2}>
          {/* Predefined Time Period Filter */}
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth disabled={useCustomDateRange}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timeRange}
                label="Time Period"
                onChange={(e) => {
                  setTimeRange(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value={7}>Last 7 Days</MenuItem>
                <MenuItem value={30}>Last 30 Days</MenuItem>
                <MenuItem value={60}>Last 60 Days</MenuItem>
                <MenuItem value={90}>Last 90 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>OR</Divider>
          </Grid>
          
          {/* Custom Date Range Filter */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item xs={12} md={6} lg={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={startDate || undefined}
              />
            </Grid>
          </LocalizationProvider>
          
          <Grid item xs={12} md={6} lg={4}>
            <Stack direction="row" spacing={2} sx={{ height: '100%', alignItems: 'center' }}>
              <Button 
                variant="contained" 
                startIcon={<FilterAltIcon />} 
                onClick={handleApplyDateFilter}
                disabled={!startDate || !endDate}
              >
                Apply Date Range
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<ClearIcon />} 
                onClick={handleClearDateFilter}
                disabled={!useCustomDateRange}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Advanced Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Call Filters
          </Typography>
          <Button 
            variant="text" 
            onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
          >
            {isAdvancedFilterOpen ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          {/* Phone number filter */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Search any phone number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Direction filter */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={direction}
                label="Direction"
                onChange={(e) => setDirection(e.target.value)}
              >
                <MenuItem value="all">All Directions</MenuItem>
                <MenuItem value="inbound">Inbound Only</MenuItem>
                <MenuItem value="outbound-api">Outbound Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Clear filters button */}
          <Grid item xs={12} md={4}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={clearAllFilters}
              sx={{ height: '100%' }}
            >
              Clear All Filters
            </Button>
          </Grid>
          
          {/* Call status checkboxes - only visible when advanced filters are open */}
          {isAdvancedFilterOpen && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Call Status
                </Typography>
                <Box display="flex" mb={1}>
                  <Button size="small" onClick={() => handleSelectAllStatuses(true)}>Select All</Button>
                  <Button size="small" onClick={() => handleSelectAllStatuses(false)}>Select None</Button>
                </Box>
                <Grid container spacing={1}>
                  {Object.keys(selectedStatuses).map(status => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={status}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={selectedStatuses[status]} 
                            onChange={() => handleStatusChange(status)}
                          />
                        }
                        label={
                          <Chip 
                            label={status} 
                            color={getStatusColor(status)} 
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Filter status display */}
      <Box mb={2}>
        {useCustomDateRange && startDate && endDate && (
          <Typography variant="subtitle2" component="span" sx={{ mr: 2 }}>
            Date: {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
          </Typography>
        )}
        {phoneNumber && (
          <Typography variant="subtitle2" component="span" sx={{ mr: 2 }}>
            Phone: {phoneNumber}
          </Typography>
        )}
        {direction !== 'all' && (
          <Typography variant="subtitle2" component="span" sx={{ mr: 2 }}>
            Direction: {direction}
          </Typography>
        )}
        {filteredCalls.length !== calls.length && (
          <Typography variant="subtitle2" component="span" color="primary">
            Showing {filteredCalls.length} of {calls.length} calls
          </Typography>
        )}
      </Box>
      
      {/* Results table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="historical call logs table">
            <TableHead>
              <TableRow>
                <TableCell>SID</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedCalls.map((call) => (
                <TableRow key={call.sid}>
                  <TableCell>{call.sid}</TableCell>
                  <TableCell>{call.from}</TableCell>
                  <TableCell>{call.to}</TableCell>
                  <TableCell>
                    <Chip 
                      label={call.direction} 
                      color={call.direction === 'inbound' ? 'primary' : 'secondary'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={call.status} 
                      color={getStatusColor(call.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{call.duration} sec</TableCell>
                  <TableCell>{call.price || '$0.00'}</TableCell>
                  <TableCell>{formatDateTime(call.startTime)}</TableCell>
                  <TableCell>{formatDateTime(call.endTime)}</TableCell>
                </TableRow>
              ))}
              {displayedCalls.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">No historical call logs found</TableCell>
                </TableRow>
              )}
              {loading && calls.length > 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}

export default HistoricalCallLogs;